import React, { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import toast from "react-hot-toast";

import { updateErrorReport } from "../api/errorReportsApi";

const BRAND = "#55C5D0";
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];

/**
 * Correct a report you filed yourself. Backend enforces the same rule
 * (reported_by must match), this just keeps the option out of view for
 * everyone else instead of showing a control that would 403.
 */
const EditIssueModal = ({ report, onClose, onSaved }) => {
  const [form, setForm] = useState({ title: "", description: "", priority: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!report) return;
    setForm({
      title: report.title || "",
      description: report.description || "",
      priority: report.priority || "",
    });
  }, [report]);

  if (!report) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Please enter a title.");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Please enter a description.");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateErrorReport(report.id, form);
      toast.success("Updated.");
      onSaved?.(updated);
      onClose?.();
    } catch (err) {
      const data = err?.response?.data;
      toast.error(
        data?.detail ||
          (Array.isArray(data?.title) && data.title[0]) ||
          (Array.isArray(data?.description) && data.description[0]) ||
          "Could not save your changes."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center bg-black/40 p-3 sm:p-4 overflow-y-auto">
      <form
        onSubmit={submit}
        className="bg-white rounded-xl shadow-xl w-full max-w-lg my-auto"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-medium text-gray-800">
            Edit your report
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What went wrong? <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              maxLength={255}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={5}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
            >
              <option value="">--Select--</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg text-white flex items-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: BRAND }}
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving...
              </>
            ) : (
              "Save changes"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditIssueModal;
